export const NAV_ROUTES = {
  dashboard: '/',
  schools: '/schools',
  roster: '/roster',
  plans: '/plans',
  reports: '/reports',
  compliance: '/compliance',
  hls: '/home-language-survey',
  messages: '/message-center',
  data: '/data',
  settings: '/settings',
  peAdmin: '/admin/program-evaluations',
  uploadsAdmin: '/admin/uploads',
  analyticsAdmin: '/admin/analytics',
  impersonationAdmin: '/admin/impersonation',
  progressReportAuditAdmin: '/admin/progress-report-audit',
  criteriaDifferencesAdmin: '/admin/criteria-differences',
  sisMatchingAdmin: '/admin/sis-matching',
  featureFlagsAdmin: '/admin/feature-flags',
} as const;

export const ANALYTICS_BETA_ROUTE = `${NAV_ROUTES.analyticsAdmin}/beta`;

export const ANALYTICS_PERFORMANCE_ROUTE = `${NAV_ROUTES.analyticsAdmin}/performance`;

export const LEGACY_BETA_USAGE_ROUTE = '/admin/beta-usage';

export type NavId = keyof typeof NAV_ROUTES;

export const COMING_SOON_IDS: NavId[] = ['compliance', 'hls', 'messages'];

export const COMING_SOON_ROUTES: string[] = COMING_SOON_IDS.map((id) => NAV_ROUTES[id]);

export function navPathForId(id: string): string | undefined {
  return (NAV_ROUTES as Record<string, string>)[id];
}

export function pathToActiveId(pathname: string): NavId | undefined {
  const entries = Object.entries(NAV_ROUTES) as [NavId, string][];
  const exact = entries.find(([, path]) => path === pathname);
  if (exact) {
    return exact[0];
  }
  const nested = entries.find(([, path]) => path !== '/' && pathname.startsWith(`${path}/`));
  return nested?.[0];
}

export function isReportsPath(pathname: string): boolean {
  return pathToActiveId(pathname) === 'reports';
}

export function isStudentsPath(pathname: string): boolean {
  return pathToActiveId(pathname) === 'roster';
}

export function isPlansPath(pathname: string): boolean {
  return pathToActiveId(pathname) === 'plans';
}

export function isSettingsPath(pathname: string): boolean {
  return pathToActiveId(pathname) === 'settings';
}

const PROGRAM_EVALUATION_PATH = '/program-evaluation';

export function isProgramEvaluationPath(pathname: string): boolean {
  return pathname === PROGRAM_EVALUATION_PATH || pathname.startsWith(`${PROGRAM_EVALUATION_PATH}/`);
}

const DATA_REVIEW_PATH = '/review';

export function isDataPath(pathname: string): boolean {
  return pathToActiveId(pathname) === 'data' || pathname === DATA_REVIEW_PATH;
}

// Benchmark monitoring drills down from the dashboard; a district admin
// reads it for their district.
const BENCHMARKS_PATH = '/benchmarks';

export function isBenchmarksPath(pathname: string): boolean {
  return pathname === BENCHMARKS_PATH || pathname.startsWith(`${BENCHMARKS_PATH}/`);
}

const ASSIGNMENT_MONITORING_PATH = '/dashboard/assignments';

export function isAssignmentMonitoringPath(pathname: string): boolean {
  return pathname.startsWith(`${ASSIGNMENT_MONITORING_PATH}/`);
}

// Flashlight 360 growth drills down from the dashboard tile; a district admin
// reads it for their district.
const GROWTH_PATH = '/dashboard/growth';

export function isGrowthPath(pathname: string): boolean {
  return pathname === GROWTH_PATH || pathname.startsWith(`${GROWTH_PATH}/`);
}

export function isDashboardPath(pathname: string): boolean {
  return (
    pathname === NAV_ROUTES.dashboard || isBenchmarksPath(pathname) || isAssignmentMonitoringPath(pathname) || isGrowthPath(pathname)
  );
}

// Links inside the app (sidebar, breadcrumbs, in-app redirects) tag their
// navigations so the dashboard can tell a Dashboard link from a login redirect
// or a typed URL, which still land a district admin on Reports.
export const APP_LINK_STATE = { appLink: true } as const;

export function cameFromAppLink(state: unknown): boolean {
  return (state as { appLink?: unknown } | null | undefined)?.appLink === true;
}

export type RouteRole = { isSuperAdmin: boolean; isDistrictAdmin: boolean };

// The router matches /data/ to /data, so the gate reads a trailing slash the same way.
function withoutTrailingSlash(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

function isOnePlanPath(pathname: string): boolean {
  return isPlansPath(pathname) && pathname !== NAV_ROUTES.plans;
}

export function isPathAllowedForRole(pathname: string, role: RouteRole): boolean {
  if (role.isSuperAdmin) return true;
  const path = withoutTrailingSlash(pathname);
  if (isReportsPath(path) || isStudentsPath(path) || isOnePlanPath(path)) return true;
  return (
    role.isDistrictAdmin &&
    (isDataPath(path) ||
      isDashboardPath(path) ||
      isPlansPath(path) ||
      isSettingsPath(path) ||
      isProgramEvaluationPath(path))
  );
}

export function isComingSoonPath(pathname: string): boolean {
  const id = pathToActiveId(pathname);
  return id !== undefined && (COMING_SOON_IDS as string[]).includes(id);
}
